<template>
  <div class="hb-root">
    <!-- toolbar: legend · metric switch · week arrows -->
    <div class="hb-toolbar">
      <div class="hb-legend">
        <span v-if="metric !== 'refunds'" class="hb-legend-item"><span class="hb-swatch is-sales"></span> Sales</span>
        <span v-if="metric !== 'sales'" class="hb-legend-item"><span class="hb-swatch is-refund"></span> Refunds</span>
      </div>

      <div class="hb-seg" role="group" aria-label="What to show">
        <button
          v-for="opt in metricOptions"
          :key="opt.value"
          type="button"
          class="hb-seg-btn"
          :class="{ 'is-active': metric === opt.value }"
          @click="metric = opt.value"
        >{{ opt.text }}</button>
      </div>

      <div v-if="nav" class="hb-nav">
        <button type="button" class="hb-nav-btn" aria-label="Previous week"
                :disabled="loading" @click="$emit('prev')">
          <b-icon icon="chevron-left"></b-icon>
        </button>
        <div class="hb-nav-label">
          <span>{{ navLabel }}</span>
          <button v-if="showReset" type="button" class="hb-nav-reset" @click="$emit('reset')">
            Back to selected week
          </button>
        </div>
        <button type="button" class="hb-nav-btn" aria-label="Next week"
                :disabled="loading || !canNext" @click="$emit('next')">
          <b-icon icon="chevron-right"></b-icon>
        </button>
      </div>
    </div>

    <div class="hb-body" :class="{ 'is-loading': loading }">
      <div v-if="!columns.length" class="hb-empty">No data for this range.</div>

      <div v-else class="hb-scroll">
        <div class="hb-grid" :style="{ gridTemplateColumns: `70px repeat(${columns.length}, minmax(118px, 1fr))` }">
          <!-- TIME on the LEFT, one row per hour -->
          <template v-for="row in grid.rows">
            <div class="hb-time" :key="'t' + row.hour">{{ row.label }}</div>
            <div
              v-for="(cell, cIdx) in row.cells"
              :key="row.hour + '-' + cIdx"
              class="hb-cell"
              :class="{
                'is-empty': !showsSales(cell) && !showsRefunds(cell),
                'is-peak': cell.peak && metric !== 'refunds'
              }"
              :style="{ '--heat': heat(cell) }"
              :title="cellTitle(row, columns[cIdx], cell)"
            >
              <div v-if="showsSales(cell)" class="hb-line">
                <span class="hb-amt is-sales">{{ money(cell.sales) }}</span>
                <span class="hb-bar"><span class="hb-fill is-sales" :style="{ width: barWidth(cell.sales, grid.maxSales) }"></span></span>
              </div>
              <div v-if="showsRefunds(cell)" class="hb-line">
                <span class="hb-amt is-refund">−{{ money(cell.refunds) }}</span>
                <span class="hb-bar"><span class="hb-fill is-refund" :style="{ width: barWidth(cell.refunds, grid.maxRefunds) }"></span></span>
              </div>
            </div>
          </template>

          <!-- DAYS / STORES along the BOTTOM -->
          <div class="hb-corner"></div>
          <div v-for="col in columns" :key="'c' + col.key" class="hb-colhead" :title="col.label">
            <span class="hb-colname">{{ col.label }}</span>
            <small v-if="col.sub">{{ col.sub }}</small>
          </div>
        </div>
      </div>

      <div v-if="grid.outside > 0" class="hb-foot">
        {{ grid.outside }} transaction{{ grid.outside === 1 ? '' : 's' }} outside 8am–9pm not shown.
      </div>

      <div v-if="loading" class="hb-loading"><b-spinner small></b-spinner> Loading week…</div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

const HOUR_START = 8;
const HOUR_END = 21;

export default {
  name: 'HourlyBarChart',
  props: {
    columns:   { type: Array, default: () => [] },     // [{ key, label, sub }]
    salesData: { type: Array, default: () => [] },
    columnKey: { type: Function, required: true },    // row => column key
    nav:       { type: Boolean, default: false },
    navLabel:  { type: String, default: '' },
    canNext:   { type: Boolean, default: true },
    showReset: { type: Boolean, default: false },
    loading:   { type: Boolean, default: false }
  },
  data() {
    return {
      metric: 'both',
      metricOptions: [
        { value: 'both',    text: 'Both' },
        { value: 'sales',   text: 'Sales' },
        { value: 'refunds', text: 'Refunds' }
      ]
    };
  },
  computed: {
    grid() {
      const colIdx = {};
      this.columns.forEach((c, i) => { colIdx[c.key] = i; });

      const rows = [];
      for (let h = HOUR_START; h <= HOUR_END; h++) {
        rows.push({
          hour: h,
          label: `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'am' : 'pm'}`,
          cells: this.columns.map(() => ({ sales: 0, refunds: 0, orders: 0, peak: false }))
        });
      }

      let outside = 0;
      for (const r of this.salesData || []) {
        if (!r.ts) continue;
        const ci = colIdx[this.columnKey(r)];
        if (ci === undefined) continue;
        const h = moment(r.ts).hour();
        if (h < HOUR_START || h > HOUR_END) { outside++; continue; }
        const amt = parseFloat(
          r.total_actualized_dollar_amount || r.total_dollar_amount || r.invoice_total || 0
        ) || 0;
        const cell = rows[h - HOUR_START].cells[ci];
        if (r.is_refund || amt < 0) cell.refunds += Math.abs(amt);
        else                        cell.sales   += amt;
        cell.orders++;
      }

      let maxSales = 0, maxRefunds = 0, peak = null;
      rows.forEach(row => row.cells.forEach(c => {
        if (c.sales > maxSales) { maxSales = c.sales; peak = c; }
        if (c.refunds > maxRefunds) maxRefunds = c.refunds;
      }));
      if (peak) peak.peak = true;

      return { rows, maxSales, maxRefunds, outside };
    }
  },
  methods: {
    showsSales(c)   { return this.metric !== 'refunds' && c.sales > 0; },
    showsRefunds(c) { return this.metric !== 'sales' && c.refunds > 0; },
    heat(c) {
      if (this.metric === 'refunds') return 0;
      return this.grid.maxSales ? (c.sales / this.grid.maxSales).toFixed(3) : 0;
    },
    barWidth(v, max) {
      if (!v || !max) return '0%';
      return `${Math.max(6, (v / max) * 100)}%`;
    },
    money(v) {
      if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
      return `$${Math.round(v || 0).toLocaleString('en-US')}`;
    },
    exact(v) {
      return `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    cellTitle(row, col, c) {
      return `${col.label}${col.sub ? ' ' + col.sub : ''} at ${row.label}\n`
        + `Sales: ${this.exact(c.sales)}\nRefunds: ${this.exact(c.refunds)}\nTransactions: ${c.orders}`;
    }
  }
};
</script>

<style scoped>
.hb-root { --hb-sales: #11A39B; --hb-sales-ink: #0B7F79; --hb-refund: #FF8347; --hb-refund-ink: #D9602A;
           --hb-ink: #221F1C; --hb-muted: #736C63; --hb-line: #E5E1DA; }

/* ─── toolbar ────────────────────────────────────────── */
.hb-toolbar { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; padding: 14px 18px 4px; }
.hb-legend { display: flex; gap: 14px; font-size: 13px; font-weight: 700; color: var(--hb-muted); }
.hb-legend-item { display: inline-flex; align-items: center; gap: 6px; }
.hb-swatch { width: 14px; height: 14px; border-radius: 4px; display: inline-block; }
.hb-swatch.is-sales  { background: var(--hb-sales); }
.hb-swatch.is-refund { background: var(--hb-refund); }

.hb-seg { display: inline-flex; background: #F1EEE9; border: 1px solid var(--hb-line); border-radius: 999px; padding: 3px; }
.hb-seg-btn { background: none; border: 0; border-radius: 999px; padding: 6px 14px; font-size: 13px; font-weight: 700; color: #4A4540; cursor: pointer; }
.hb-seg-btn.is-active { background: var(--hb-ink); color: #fff; }

.hb-nav { margin-left: auto; display: flex; align-items: center; gap: 8px; }
.hb-nav-btn {
  width: 38px; height: 38px; border-radius: 50%;
  border: 1.5px solid var(--hb-line); background: #fff; color: var(--hb-ink);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 16px; cursor: pointer; transition: border-color 0.15s ease, color 0.15s ease;
}
.hb-nav-btn:hover:not(:disabled) { border-color: #EE7A22; color: #D4620F; }
.hb-nav-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.hb-nav-label {
  min-width: 170px; text-align: center; line-height: 1.2;
  font-family: 'Sora', sans-serif; font-weight: 700; font-size: 14px; color: var(--hb-ink);
  display: flex; flex-direction: column; align-items: center;
}
.hb-nav-reset { background: none; border: 0; padding: 0; margin-top: 2px; font-family: inherit; font-size: 11.5px; font-weight: 700; color: #D4620F; cursor: pointer; }
.hb-nav-reset:hover { text-decoration: underline; }
.hb-seg-btn:focus-visible, .hb-nav-btn:focus-visible, .hb-nav-reset:focus-visible { outline: 2px solid #EE7A22; outline-offset: 2px; }

/* ─── grid: time rows × day/store columns ───────────── */
.hb-body { position: relative; padding: 10px 18px 18px; }
.hb-body.is-loading .hb-scroll { opacity: 0.35; pointer-events: none; }
.hb-loading { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 8px; font-weight: 700; color: var(--hb-muted); }

.hb-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.hb-grid { display: grid; gap: 5px; }

.hb-time {
  position: sticky; left: 0; z-index: 2; background: #fff;
  display: flex; align-items: center; justify-content: flex-end; padding-right: 12px;
  font-family: 'Sora', sans-serif; font-weight: 800; font-size: 14px; color: var(--hb-ink);
  border-right: 2px solid #D9D4CC;
}

.hb-cell {
  min-height: 56px; padding: 7px 10px;
  border-radius: 10px;
  background: rgba(17, 163, 155, calc(var(--heat) * 0.16));
  border: 1px solid rgba(17, 163, 155, calc(0.08 + var(--heat) * 0.3));
  display: flex; flex-direction: column; justify-content: center; gap: 5px;
}
.hb-cell.is-empty { background: #FAF9F7; border: 1px dashed #ECE8E2; }
.hb-cell.is-peak { box-shadow: inset 0 0 0 2px #EE7A22; }

.hb-line { display: flex; flex-direction: column; gap: 3px; }
.hb-amt {
  font-family: 'Sora', sans-serif; font-weight: 800; font-size: 15px; line-height: 1.1;
  font-variant-numeric: tabular-nums; white-space: nowrap;
}
.hb-amt.is-sales  { color: var(--hb-sales-ink); }
.hb-amt.is-refund { color: var(--hb-refund-ink); font-size: 13.5px; }
.hb-bar { display: block; height: 7px; border-radius: 4px; background: rgba(34, 31, 28, 0.06); overflow: hidden; }
.hb-fill { display: block; height: 100%; border-radius: 4px; }
.hb-fill.is-sales  { background: var(--hb-sales); }
.hb-fill.is-refund { background: var(--hb-refund); }

.hb-corner { position: sticky; left: 0; z-index: 2; background: #fff; }
.hb-colhead {
  margin-top: 4px; padding-top: 10px; border-top: 2px solid #D9D4CC;
  text-align: center; min-width: 0;
  font-family: 'Sora', sans-serif; font-weight: 800; font-size: 14px; color: var(--hb-ink);
}
.hb-colname { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.hb-colhead small { display: block; font-weight: 600; font-size: 12px; color: var(--hb-muted); margin-top: 2px; }

.hb-foot { margin-top: 12px; font-size: 12.5px; color: var(--hb-muted); }
.hb-empty { padding: 40px; text-align: center; color: var(--hb-muted); }

@media (max-width: 767px) {
  .hb-nav { margin-left: 0; width: 100%; justify-content: space-between; }
  .hb-body { padding: 8px 10px 14px; }
}
</style>


// WEBPACK FOOTER //
// src/components/Reports/HourlyBarChart.vue